import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Form, Space } from 'antd';
import { usePurchaseOrderStore } from '../../../../store/PurchaseOrderStore';
import React, { useEffect, useState } from 'react';
import { useFetchData, useFetchFile, useUpdateData } from '../../../../hooks';
import { BaseModel, FormField, PageableResponse } from '../../../../types';
import { Currency, Resource } from '../../../../lib/enums';
import {
  initialPurchaseOrderForm,
  PurchaseOrderForm,
  PurchaseOrderInfo,
} from '../../../../types/PurchaseOrder';
import { v4 as uuidv4 } from 'uuid';
import {
  useDeletePOFile,
  useUploadPOFiles,
} from '../../../../hooks/usePurchaseOrderHooks';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../lib/utils';
import ErrorModal from '../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../components/ui/modal/InfoModal';
import CustomForm from '../../../../components/ui/CustomForm';
import OrderLineFormEditPage from './OrderLineFormEditPage';
import PageHeaderAction from '../../../../components/PageHeaderAction';
import SubmitButton from '../../../../components/ui/SubmitButton';
import PurchaseOrderInfoViewSkeleton from '../../info/PurchaseOrderInfoViewSkeleton';

const PurchaseOrderFormEditPage = () => {
  const api = '/purchase/orders';
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const purchaseOrderStore = usePurchaseOrderStore();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState('');

  const { isLoading, data, isError, error } = useFetchData<PurchaseOrderInfo>(
    `${api}/${params.id}`,
  );

  const { data: dataFiles } = useFetchFile(
    `${api}/${params.id}/files`,
    'api/purchase/orders',
  );
  const mutationDelete = useDeletePOFile();
  const {
    mutate: mutate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpadte,
    isPending,
  } = useUpdateData(`${api}`);
  const {
    mutate: mutateFile,
    isError: isFileError,
    error: fileError,
  } = useUploadPOFiles();

  const { data: purchaseTermData, isLoading: isPurchaseTermLoading } =
    useFetchData<PageableResponse<BaseModel>>('/purchase/terms', {
      resource: searchParams.get('resource') || Resource.FULL,
      length: parseInt(searchParams.get('length') || '999999'),
    });

  const { data: vendorData, isLoading: isVendorLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/partner/vendors', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  const currencyOptions: BaseModel[] = Object.keys(Currency).map(
    (key) =>
      ({
        id: key,
        code: Currency[key as keyof typeof Currency],
        description: Currency[key as keyof typeof Currency],
      }) as BaseModel,
  );

  useEffect(() => {
    if (data !== undefined) {
      data.orderLines.map((line) => {
        line.uuid = uuidv4();
      });
      purchaseOrderStore.setFormData(data);
    }
    if (isError) {
      setIsErrorModalOpen(true);
      setErrorMess(error.message);
    }
    if (isFileError) {
      setIsErrorModalOpen(true);
      setErrorMess(fileError.message);
    }
  }, [data, isError]);

  useEffect(() => {
    if (isSuccessUpdate) setIsInfoModalOpen(true);
    if (isErrorUpdate) {
      setIsErrorModalOpen(true);
      // The server's reason ("A vendor is required.") beats axios's generic
      // "Request failed with status code 400".
      setErrorMess(
        errorUpadte?.response?.data?.message ??
          errorUpadte?.message ??
          'Could not save the purchase order.',
      );
    }
  }, [isSuccessUpdate, isErrorUpdate]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const handleDeleteFile = (file: any) => {
    mutationDelete.mutate(file.uid);
  };
  const onFinish = (values: any) => {
    const vendor: BaseModel | undefined = vendorData?.data.find((obj) => {
      return obj.id == values.vendor;
    });
    const destination: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.destination;
      },
    );

    const terms: BaseModel | undefined = purchaseTermData?.data.find((obj) => {
      return obj.id == values.terms;
    });

    const payload: PurchaseOrderForm = {
      ...values,
      id: params.id,
      vendor: vendor,
      destination: destination,
      terms: terms,
      orderDate: dayjs(values.orderDate).format(dateFormat),
      requiredDate: dayjs(values.requiredDate).format(dateFormat),
      orderLines: purchaseOrderStore.formData.orderLines || [],
    };

    mutate({
      id: params.id,
      form: payload,
    });
    if (isSuccessUpdate) purchaseOrderStore.setFormData(payload);
    if (values.files !== undefined) {
      const fileList = values.files.fileList;
      const formData = new FormData();
      fileList.forEach((file: any) => {
        formData.append('files', file.originFileObj);
      });
      mutateFile({ id: params.id, formData: formData });
    }
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate(`/purchase-order/${params.id}`);
  };

  const fileUploads = dataFiles?.map((file) => ({
    uid: file.id,
    name: file.origFilename,
    status: 'done',
    url: file.code,
  }));

  const handleCancel = () => {
    purchaseOrderStore.setFormData(initialPurchaseOrderForm);
    navigate(`/purchase-order/${params.id}`);
  };

  const fields: FormField[] = [
    {
      name: 'code',
      label: 'Code',
      disabled: true,
      type: 'input',
      rules: [{ required: true, message: 'Please select code' }],
      defaultValue: data?.code,
    },
    {
      name: 'orderDate',
      label: 'Order Date',
      rules: [{ required: true, message: 'Please select order date' }],
      type: 'datepicker',
      defaultValue: data?.orderDate,
    },
    {
      name: 'requiredDate',
      label: 'Required Date',
      rules: [{ required: true, message: 'Please select required date' }],
      type: 'datepicker',
      defaultValue: data?.requiredDate,
    },
    {
      name: 'vendor',
      label: 'Vendor',
      type: 'select',
      isLoading: isVendorLoading,
      isMultiple: false,
      values: vendorData?.data,
      rules: [{ required: true, message: 'Please select vendor' }],
      defaultValue: data?.vendor?.id,
    },
    {
      name: 'destination',
      label: 'Destination',
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select destination' }],
      defaultValue: data?.destination?.id,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'radio-group-button',
      defaultValue: data?.currency,
      values: currencyOptions,
    },
    {
      name: 'terms',
      label: 'Terms',
      type: 'select',
      isMultiple: false,
      isLoading: isPurchaseTermLoading,
      values: purchaseTermData?.data,
      defaultValue: data?.terms?.id,
    },
    {
      name: 'source',
      label: 'Source Document',
      type: 'input',
      defaultValue: data?.source,
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      defaultValue: data?.source,
    },
    {
      name: 'status',
      label: 'Status',
      disabled: true,
      type: 'input',
      defaultValue: data?.status,
    },
    {
      name: 'files',
      label: 'Attachments',
      disabled: false,
      type: 'file',
      defaultValue: fileUploads,
    },
  ];

  return (
    <>
      <div className={'flex items-center justify-between'}>
        <PageHeaderAction action={'Request'} moduleName={'Purchase Order'} />
        <Space>
          <Button onClick={handleCancel}>Close</Button>
          <SubmitButton loading={isPending} form={form}>
            Submit
          </SubmitButton>
        </Space>
      </div>
      <div className="bg-white mt-4 h-[calc(84vh)] overflow-auto">
        {isLoading ? (
          <div className="p-4">
            <PurchaseOrderInfoViewSkeleton active={true} />
            <div className={'text-base font-semibold leading-6'}>
              Order Lines
            </div>
          </div>
        ) : (
          <div>
            <div className="p-4">
              <CustomForm
                onDeleteFile={handleDeleteFile}
                data={{ ...data, files: fileUploads }}
                formFields={fields}
                form={form}
                onFinish={onFinish}
                rowCol={3}
                layout={'vertical'}
                formName={'po_form_update'}
              />
            </div>
            <OrderLineFormEditPage />
          </div>
        )}
      </div>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={'Purchase order successfully updated'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default PurchaseOrderFormEditPage;
